<template>
  <div 
    class="job-card"
    :class="[job.borderClass, { 'dragging-ghost': isDragging, 'overdue': isOverdue, 'selected': isSelected }]"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="onClick"
  >
    <!-- Job Header (spacing tightened) -->
    <div class="d-flex align-items-start mb-1">
      <h6 class="mb-0 fw-bold text-primary">{{ job.id }} </h6>
      <span v-if="shouldShowOverdueStatus" class="overdue-status-text">
        - Due {{ toTitleCase(job.displayDueDateStatus) }}
      </span>
      <span v-else-if="hasNonOverdueDueDate" class="due-date-text">
        - Due {{ formattedDueDate }}
      </span>
    </div>

  <!-- Client (slightly bolder) -->
  <p class="mb-1 company-name">{{ job.client }}</p>

    <!-- Location -->
    <p class="mb-2 text-muted job-location" style="font-size: 0.85em;">
      {{ job.location }}
    </p>

    <!-- Status Badge - REMOVED (moved to header) -->
    <!-- <div class="mb-2" v-if="job.htmlViewStatus" v-html="job.htmlViewStatus"></div> -->

    <!-- Job Details Row (status + technician status + type pills grouped) -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="pill-group" v-if="job.status || job.technicianStatus || job.type">
        <span v-if="job.status" class="custom-status-pill" :style="statusPillStyle">{{ toTitleCase(job.status) }}</span>
        <span v-if="job.technicianStatus" class="custom-tech-status-pill" :style="technicianStatusPillStyle">{{ toTitleCase(job.technicianStatus) }}</span>
        <span v-if="job.type" class="custom-type-pill" :style="typePillStyle">{{ toTitleCase(job.type) }}</span>
      </div>
      <span v-if="job.total && job.total !== '0.00'" class="fw-bold text-success">${{ formatCurrency(job.total) }}</span>
    </div>

    <!-- Due Date Row - REMOVED (now showing via border color) -->
    <!-- <div v-if="job.htmlViewDueDate" class="mb-2">
      <small class="text-muted">Due: </small>
      <span v-html="job.htmlViewDueDate"></span>
    </div> -->

    <!-- Contact Info - HIDDEN -->
    <!-- <div v-if="job.primaryContactName" class="mb-2">
      <small class="text-muted">
        Contact: {{ job.primaryContactName }}
        <span v-if="job.primaryContactPhone">({{ job.primaryContactPhone }})</span>
      </small>
    </div> -->

    <!-- Bottom Row - Only show assignee, hide created date -->
    <div class="d-flex justify-content-between align-items-center">
      <small class="text-muted" v-if="job.assignee && job.assignee !== 'Unassigned'">
        {{ job.assignee }}
      </small>
    </div>

    <!-- Created By - HIDDEN -->
    <!-- <div v-if="job.createdBy" class="mt-1">
      <small class="text-muted">By: {{ job.createdBy }}</small>
    </div> -->

    <!-- Drag indicator -->
    <div class="drag-indicator">
      <i class="fa fa-grip-vertical"></i>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JobCard',
  props: {
    job: {
      type: Object,
      required: true
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDragging: false
    };
  },
  computed: {
    isOverdue() {
      // Don't show as overdue if job is completed
      if (this.isCompleted) {
        return false;
      }
      
      // Check if the job has a due date and if it's past due
      if (!this.job.dueDate && !this.job.displayDueDate) {
        return false;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today
      
      let dueDate;
      
      // Try to parse from different date fields
      if (this.job.dueDate) {
        dueDate = new Date(this.job.dueDate);
      } else if (this.job.displayDueDate) {
        dueDate = new Date(this.job.displayDueDate);
      }
      
      if (!dueDate || isNaN(dueDate.getTime())) {
        return false;
      }
      
      dueDate.setHours(0, 0, 0, 0); // Set to start of due date
      
      return dueDate < today;
    },

    isCompleted() {
      // Check if the job is in completed status
      // Using both status ID (7 is the hard-coded completed status ID) and status name
      return this.job.intProjectStatusID === 7 || 
             (this.job.status && this.job.status.toLowerCase().includes('completed'));
    },

    shouldShowOverdueStatus() {
      return this.isOverdue && 
             this.job.displayDueDateStatus && 
             !this.isCompleted;
    },

    statusPillStyle() {
      if (!this.job.status) {
        return {};
      }

      // Use the htmlColourStatus if available, otherwise default to info blue
      const backgroundColor = this.job.htmlColourStatus || '#17a2b8';
      
      // Determine text color based on background
      const isLight = this.isLightColor(backgroundColor);
      const textColor = isLight ? '#212529' : '#ffffff';

      return {
        backgroundColor,
        color: textColor
      };
    },

  typePillStyle() {
      if (!this.job.type) {
        return {};
      }

      // Use the htmlColourProjectType if available, otherwise default to light gray-blue
      const backgroundColor = this.job.htmlColourProjectType || '#e2e6ea';
      
      // Use dark gray text color for better visibility, but only for the default background
      const textColor = this.job.htmlColourProjectType ? 
        (this.isLightColor(backgroundColor) ? '#212529' : '#ffffff') : 
        '#495057';

      return {
        backgroundColor,
        color: textColor
      };
    },
    technicianStatusPillStyle() {
      if (!this.job.technicianStatus) return {};
      const backgroundColor = this.job.htmlColourTechnicianStatus || '#9A359A';
      const isLight = this.isLightColor(backgroundColor);
      const textColor = isLight ? '#212529' : '#ffffff';
      return { backgroundColor, color: textColor };
    },

    // Check if job has a due date and is not past due
    hasNonOverdueDueDate() {
      // Don't show if job is completed
      if (this.isCompleted) {
        return false;
      }
      
      // Check if the job has a due date
      if (!this.job.dueDate && !this.job.displayDueDate) {
        return false;
      }
      
      // Return true if job has due date but is not overdue
      return !this.isOverdue;
    },

    // Format due date in relative format (Today, Tomorrow, This Week Wed, etc.)
    formattedDueDate() {
      if (!this.hasNonOverdueDueDate) {
        return '';
      }

      let dueDate;
      
      // Try to parse from different date fields
      if (this.job.dueDate) {
        dueDate = new Date(this.job.dueDate);
      } else if (this.job.displayDueDate) {
        dueDate = new Date(this.job.displayDueDate);
      }
      
      if (!dueDate || isNaN(dueDate.getTime())) {
        return '';
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const dueDateCopy = new Date(dueDate);
      dueDateCopy.setHours(0, 0, 0, 0);
      
      const diffTime = dueDateCopy.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      // Today
      if (diffDays === 0) {
        return 'Today';
      }
      
      // Tomorrow
      if (diffDays === 1) {
        return 'Tomorrow';
      }
      
      // Check if due date is within the current calendar week
      // Calculate the end of this week (Saturday at 11:59 PM)
      const endOfWeek = new Date(today);
      const daysUntilSaturday = (6 - today.getDay()) % 7;
      endOfWeek.setDate(today.getDate() + daysUntilSaturday);
      endOfWeek.setHours(23, 59, 59, 999);
      
      // This week (within current calendar week, but not today/tomorrow)
      if (dueDateCopy <= endOfWeek && diffDays >= 2) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayName = dayNames[dueDate.getDay()];
        return `This ${dayName}`;
      }
      
      // Check if due date is within next week
      // Calculate the end of next week (Saturday of next week at 11:59 PM)
      const endOfNextWeek = new Date(endOfWeek);
      endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);
      
      // Next week (within the week following current week)
      if (dueDateCopy <= endOfNextWeek) {
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const dayName = dayNames[dueDate.getDay()];
        return `Next ${dayName}`;
      }
      
      // More than current week away - show full date in DD-MMM-YYYY format
      const day = dueDate.getDate().toString().padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[dueDate.getMonth()];
      const year = dueDate.getFullYear();
      
      return `${day}-${month}-${year}`;
    }
  },
  methods: {
    onClick() {
      this.$emit('click', this.job);
    },

    onDragStart(event) {
      this.isDragging = true;
      
      // Create a custom drag image with full opacity and shadow
      const dragImage = this.createDragImage();
      if (dragImage && event.dataTransfer) {
        event.dataTransfer.setDragImage(dragImage, dragImage.offsetWidth / 2, dragImage.offsetHeight / 2);
      }
      
      // Set drag data
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', this.job.id);
        event.dataTransfer.effectAllowed = 'move';
      }
      
      this.$emit('dragstart', this.job);
    },

    createDragImage() {
      // Clone the current card element
      const originalCard = this.$el;
      const clonedCard = originalCard.cloneNode(true);
      
      // Style the clone for drag image
      clonedCard.style.position = 'absolute';
      clonedCard.style.top = '-1000px';
      clonedCard.style.left = '-1000px';
      clonedCard.style.width = originalCard.offsetWidth + 'px';
      clonedCard.style.height = originalCard.offsetHeight + 'px';
      clonedCard.style.transform = 'rotate(2deg) scale(1.03)';
      clonedCard.style.opacity = '1';
      clonedCard.style.zIndex = '9999';
      // Apply the beautiful shadow
      clonedCard.style.boxShadow = `
        0 14px 33px -16px rgba(0, 0, 0, 0.22),
        0 8px 25px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(0, 0, 0, 0.05)
      `;
      clonedCard.style.borderRadius = '0.375rem';
      clonedCard.style.background = '#fff';
      
      // Remove any dragging-ghost class from clone
      clonedCard.classList.remove('dragging-ghost');
      
      // Append to body temporarily
      document.body.appendChild(clonedCard);
      
      // Clean up after a short delay
      setTimeout(() => {
        if (document.body.contains(clonedCard)) {
          document.body.removeChild(clonedCard);
        }
      }, 100);
      
      return clonedCard;
    },

    onDragEnd() {
      // Don't immediately reset dragging state - wait for parent to confirm operation completed
      // This prevents the visual "snap back" effect during API calls
      this.$emit('dragend', this.job);
      
      // Fallback: reset dragging state after 5 seconds if no confirmation received
      setTimeout(() => {
        if (this.isDragging) {
          this.isDragging = false;
        }
      }, 5000);
    },

    // Method to be called by parent when drag operation completes
    resetDragState() {
      this.isDragging = false;
    },

    getPriorityBadgeClass(priority) {
      switch (priority) {
        case "High":
          return "badge-danger";
        case "Medium":
          return "badge-warning";
        case "Low":
          return "badge-success";
        default:
          return "badge-secondary";
      }
    },

    formatCurrency(amount) {
      return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(date) {
      if (!date) return "";
      
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) return "1 day ago";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString();
    },

    toTitleCase(str) {
      if (!str) return '';
      return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
    },

    isLightColor(color) {
      // Convert color to RGB values and calculate luminance
      let r, g, b;
      
      if (color.startsWith('#')) {
        // Hex color
        const hex = color.slice(1);
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      } else if (color.startsWith('rgb')) {
        // RGB color
        const values = color.match(/\d+/g);
        if (values && values.length >= 3) {
          r = parseInt(values[0]);
          g = parseInt(values[1]);
          b = parseInt(values[2]);
        } else {
          return false; // default to dark text
        }
      } else {
        return false; // default to dark text for unknown formats
      }
      
      // Calculate relative luminance
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
  }
};
</script>

<style scoped>
.job-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  position: relative;
  user-select: none;
}

.job-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.job-card:hover .drag-indicator {
  opacity: 1;
}

.job-card.dragging-ghost {
  opacity: 0.15; /* Very faint for the card that stays behind */
  transform: rotate(-1deg); /* Slight twist */
  transition: all 0.2s ease; /* Smooth transition to ghost state */
}

.job-card.overdue {
  background-color: rgba(220, 53, 69, 0.08) !important;
  border-left: 2px solid #dc3545 !important;
}

.job-card.overdue:hover {
  background-color: rgba(220, 53, 69, 0.12) !important;
  border-left: 2px solid #c82333 !important;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
}

.overdue-status-text {
  font-size: 0.7rem;
  color: #dc3545;
  font-weight: 500;
  white-space: nowrap;
  margin-left: 0.25rem;
}

.company-name {
  font-weight: 700 !important; /* Extra bold */
}

.job-location {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

/* Badge sizes */
.badge-xs {
  font-size: 0.65em;
  padding: 0.2em 0.4em;
}

/* Border color classes for job cards */
.border-left-orange {
  border-left: 2px solid #fd7e14 !important;
}

.border-left-blue {
  border-left: 2px solid #007bff !important;
}

.border-left-purple {
  border-left: 2px solid #6f42c1 !important;
}

.border-left-yellow {
  border-left: 2px solid #ffc107 !important;
}

.border-left-grey {
  border-left: 2px solid #6c757d !important;
}

.border-left-green {
  border-left: 2px solid #28a745 !important;
}

.border-left-teal {
  border-left: 2px solid #20c997 !important;
}

/* Drag indicator */
.drag-indicator {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #6c757d;
  font-size: 0.8rem;
  pointer-events: none;
}

/* Focus and accessibility */
.job-card:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* Priority badge colors */
.badge-danger {
  background-color: #dc3545;
  color: white;
}

.badge-warning {
  background-color: #ffc107;
  color: #212529;
}

.badge-success {
  background-color: #28a745;
  color: white;
}

.badge-secondary {
  background-color: #6c757d;
  color: white;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
}

/* Unified pill styling */
.custom-status-pill,
.custom-type-pill,
.custom-tech-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.24rem 0.5rem;
  border-radius: 4px; /* reduced rounding */
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.25px;
  white-space: nowrap;
  text-transform: none;
}

/* (Removed subtle-status override; status pill now uses API color) */

.pill-group { 
  display: flex; 
  gap: 0.25rem; 
  flex-wrap: wrap; 
}

/* Due date styling for non-overdue jobs */
.due-date-text {
  color: #8A7EC6; /* Light purple-blue color */
  font-weight: 500;
  font-size: 0.8rem;
  white-space: nowrap;
  margin-left: 0.25rem;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .job-card {
    padding: 0.5rem;
  }
  
  .drag-indicator {
    opacity: 1; /* Always show on mobile */
  }
}

/* Selected job card styling */
.job-card.selected {
  border: 2px solid #007bff !important;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  transform: translateY(-1px);
  background-color: rgba(0, 123, 255, 0.05);
}

.job-card.selected::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #007bff, #17a2b8);
  border-radius: inherit;
  z-index: -1;
  opacity: 0.1;
}
</style>
